import { useState, useMemo, useRef, useEffect } from 'react';
import { getReferenceTypeFromProperty } from 'reference-resolvers-lite/utils/getReferenceTypeFromProperty';
import { STATUS_ERROR, STATUS_IDLE, STATUS_LOADING, STATUS_SUCCESS } from 'reference-resolvers-lite/Status';
import createReferenceTypeResolver from 'automation-ui-action-features/external-options/createReferenceTypeResolver';
import { fetchAllExternalOptions } from 'automation-ui-action-features/external-options/fetchAllExternalOptions';
import { jsx as _jsx } from "react/jsx-runtime";
function useReferenceResolver({
  property,
  propertyTypeId
}) {
  if (!property || !propertyTypeId) {
    return null;
  }
  const ReferenceResolver = createReferenceTypeResolver({
    property,
    objectTypeId: propertyTypeId
  });
  return ReferenceResolver;
}

/* For resolving external option values, including multivalues separated by semicolons */
export function useExternalOptions({
  property,
  propertyTypeId,
  externalOptionValue
}) {
  const statusRef = useRef(STATUS_IDLE);
  const [references, setReferences] = useState();
  const [error, setError] = useState();
  useEffect(() => {
    statusRef.current = STATUS_IDLE;
    setReferences(undefined);
    setError(undefined);
  }, [property, propertyTypeId, externalOptionValue]);
  const ReferenceResolver = useReferenceResolver({
    property,
    propertyTypeId
  });
  const hasReferenceToFetch = ReferenceResolver && externalOptionValue;
  if (hasReferenceToFetch && statusRef.current === STATUS_IDLE) {
    statusRef.current = STATUS_LOADING;
    Promise.all(String(externalOptionValue).split(';').map(optionValue => ReferenceResolver.api.byId(optionValue))).then(setReferences).catch(setError);
  }
  if (references) {
    statusRef.current = STATUS_SUCCESS;
  } else if (error) {
    statusRef.current = STATUS_ERROR;
  }
  const status = statusRef.current;
  return useMemo(() => ({
    status,
    references,
    error
  }), [status, references, error]);
}
export const wireExternalOptionValues = WrappedComponent => props => {
  const {
    property,
    propertyTypeId,
    externalOptionValue
  } = props;
  const externalOptionValues = useExternalOptions({
    property,
    propertyTypeId,
    externalOptionValue
  });
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    externalOptionValues: externalOptionValues
  }));
};
export function useAllExternalOptions({
  property,
  propertyTypeId
}) {
  const statusRef = useRef(STATUS_IDLE);
  const [references, setReferences] = useState();
  const [error, setError] = useState();
  const ReferenceResolver = useReferenceResolver({
    property,
    propertyTypeId
  });
  if (ReferenceResolver && statusRef.current === STATUS_IDLE) {
    statusRef.current = STATUS_LOADING;
    const referenceType = getReferenceTypeFromProperty(property, propertyTypeId);
    fetchAllExternalOptions(ReferenceResolver, referenceType).then(result => setReferences(result.references)).catch(setError);
  }
  if (references) {
    statusRef.current = STATUS_SUCCESS;
  } else if (error) {
    statusRef.current = STATUS_ERROR;
  }
  const status = statusRef.current;
  return useMemo(() => ({
    status,
    references,
    error
  }), [status, references, error]);
}

/**
 * By using the reference-resolvers-lite library, we
 * leverage the new ExternalOptions service to resolve
 * property definition's external options. If a property
 * does not have a `referenceType`, it means it's either
 * missing from the service/lite library, or the property
 * definition is incorrect.
 *
 * Required props:
 *    - property: CRM property definition
 *    - propertyTypeId: objectTypeId associated with the
 *      property
 *
 * Injected props:
 *   - allExternalOptions:
 *      return a resolver with all available external
 *      options
 */

export const wireExternalOptions = WrappedComponent => props => {
  const {
    property,
    propertyTypeId
  } = props;
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    allExternalOptions: useAllExternalOptions({
      property,
      propertyTypeId
    })
  }));
};